import { AuthApi } from '../api/auth-api';
import { ModalComponent } from '../components/modal';
import { ToastComponent } from '../components/toast';
import { evaluatePasswordRules } from '../utils/password-validator';
import { UserProfile } from '../types';

export class AccountView {
  constructor(private onProfileUpdated: (user: UserProfile) => void) {
    this.initEventListeners();
  }

  private initEventListeners(): void {
    document.getElementById('closeMyAccountModalBtn')?.addEventListener('click', () => {
      ModalComponent.closeMyAccountModal();
    });

    const pwdInput = document.getElementById('accountNewPassword') as HTMLInputElement | null;
    pwdInput?.addEventListener('input', () => this.updatePasswordCriteriaUI(pwdInput.value));

    const profileForm = document.getElementById('myAccountForm') as HTMLFormElement | null;
    profileForm?.addEventListener('submit', (e) => this.handleUpdateProfile(e));

    const pwdForm = document.getElementById('changePasswordForm') as HTMLFormElement | null;
    pwdForm?.addEventListener('submit', (e) => this.handleChangePassword(e));
  }

  public open(user: UserProfile): void {
    ModalComponent.openMyAccountModal(user);
  }

  private updatePasswordCriteriaUI(password: string): void {
    const rules = evaluatePasswordRules(password);
    this.setRuleValid('accountRuleLength', rules.minLength);
    this.setRuleValid('accountRuleUpper', rules.hasUpper);
    this.setRuleValid('accountRuleDigit', rules.hasDigit);
    this.setRuleValid('accountRuleSpecial', rules.hasSpecial);

    const tag = document.getElementById('accountStrengthTag');
    if (!tag) return;

    if (rules.score === 0) {
      tag.textContent = 'En attente';
      tag.style.color = 'var(--text-dim)';
    } else if (rules.score <= 2) {
      tag.textContent = 'Insuffisant';
      tag.style.color = '#f87171';
    } else if (rules.score === 3) {
      tag.textContent = 'Moyen';
      tag.style.color = '#fbbf24';
    } else {
      tag.textContent = 'Sécurisé ✓';
      tag.style.color = '#34d399';
    }
  }

  private setRuleValid(elementId: string, isValid: boolean): void {
    const el = document.getElementById(elementId);
    if (!el) return;
    const icon = el.querySelector('.rule-icon');
    if (isValid) {
      el.classList.remove('invalid');
      el.classList.add('valid');
      if (icon) icon.textContent = '✓';
    } else {
      el.classList.remove('valid');
      el.classList.add('invalid');
      if (icon) icon.textContent = '✕';
    }
  }

  private async handleUpdateProfile(e: Event): Promise<void> {
    e.preventDefault();
    const email = (document.getElementById('accountEmail') as HTMLInputElement).value.trim();
    const telephone = (document.getElementById('accountTelephone') as HTMLInputElement).value.trim();

    try {
      const res = await AuthApi.updateProfile({ email, telephone });
      ToastComponent.show('Vos informations ont été mises à jour.', 'success');
      this.onProfileUpdated(res.user);
    } catch (err: any) {
      ToastComponent.show(err.message || 'Erreur lors de la mise à jour.', 'error');
    }
  }

  private async handleChangePassword(e: Event): Promise<void> {
    e.preventDefault();
    const currentPassword = (document.getElementById('accountCurrentPassword') as HTMLInputElement).value;
    const newPassword = (document.getElementById('accountNewPassword') as HTMLInputElement).value;

    try {
      await AuthApi.changePassword({ currentPassword, newPassword });
      ToastComponent.show('Mot de passe modifié avec succès.', 'success');
      (document.getElementById('changePasswordForm') as HTMLFormElement).reset();
      this.updatePasswordCriteriaUI('');
    } catch (err: any) {
      ToastComponent.show(err.message || 'Erreur lors du changement de mot de passe.', 'error');
    }
  }
}
